import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  CheckCircle, ArrowRight, ArrowSquareOut, CaretDown, ShieldWarning,
  Scales, ListChecks, HeartStraight, UploadSimple, GraduationCap,
} from "@phosphor-icons/react";

const label = { color: "var(--bk-text-2)" };
const card = { borderColor: "var(--bk-border)" };

function BoolPick({ value, onChange, testid }) {
  return (
    <div className="flex gap-2 mt-1.5">
      {[["Yes", true], ["No", false]].map(([t, v]) => (
        <button key={t} type="button" data-testid={`${testid}-${t.toLowerCase()}`} onClick={() => onChange(v)}
          className="px-4 py-1.5 rounded-full text-xs font-semibold border"
          style={{ borderColor: value === v ? "var(--bk-accent)" : "var(--bk-border)", color: value === v ? "var(--bk-accent)" : "var(--bk-text-2)", background: value === v ? "var(--bk-page)" : "#fff" }}>
          {t}
        </button>
      ))}
    </div>
  );
}

export function FormationWizard({ onSaved, onCancel, partner }) {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [phase, setPhase] = useState("registered"); // registered -> questions -> structure -> checklist
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [a, setA] = useState({
    registered_status: "", business_name: "", state: "", num_owners: 1,
    already_operating: null, will_have_employees: null, personal_services: null,
    professional_advice: null, wellness_services: null, structure: "", tax_treatment: "default",
  });

  useEffect(() => {
    api.get("/formation/wizard/config").then((r) => setCfg(r.data)).catch(() => toast.error("Couldn't load the formation wizard."));
  }, []);

  if (!cfg) return <p className="text-sm" style={label}>Loading…</p>;

  const verifying = a.registered_status === "yes";
  const structures = cfg.structures.filter((s) =>
    a.num_owners > 1 ? s.max_owners !== 1 : s.min_owners <= 1
  );
  const chosen = cfg.structures.find((s) => s.key === a.structure);

  const generate = async () => {
    if (!a.state) { toast.error("Choose your state first."); return; }
    if (!a.structure) { toast.error("Pick a legal structure first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/formation/wizard/checklist", a);
      setResult(data);
      setPhase("checklist");
    } catch { toast.error("Couldn't build your checklist."); }
    finally { setBusy(false); }
  };

  const entityTypeFor = () => {
    if (a.structure === "sole_proprietorship") return "Sole Proprietorship";
    if (a.structure === "partnership") return "Partnership";
    if (a.structure === "corporation") return "C-Corporation";
    return a.num_owners > 1 ? "Multi-Member LLC" : "Single-Member LLC";
  };

  const save = async (completed) => {
    if (!a.business_name.trim()) { toast.error("Enter the business name."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/formation/applications", {
        business_name: a.business_name, state: a.state, entity_type: entityTypeFor(),
        legal_structure: a.structure, tax_treatment: a.tax_treatment, wizard_answers: a,
      });
      if (completed) {
        await api.patch(`/formation/applications/${data.id}`, { status: "complete" });
      }
      toast.success(completed ? "Marked as already completed — upload your documents to the Vault to finish." : "Saved — track your formation below.");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save."); }
    finally { setBusy(false); }
  };

  return (
    <section className="bk-card p-6 mb-8" data-testid="formation-wizard">
      {/* Phase 1: registered? */}
      {phase === "registered" && (
        <div>
          <h2 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Is your business already legally registered?</h2>
          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            {cfg.registered_options.map((o) => (
              <button key={o.key} type="button" data-testid={`wizard-registered-${o.key}`}
                onClick={() => { setA((f) => ({ ...f, registered_status: o.key })); setPhase("questions"); }}
                className="rounded-xl border p-4 text-left text-sm font-semibold hover:shadow-sm transition-shadow" style={{ ...card, color: "var(--bk-text)" }}>
                {o.label}
              </button>
            ))}
          </div>
          {a.registered_status === "not_sure" && null}
          <button onClick={onCancel} className="mt-4 text-sm font-semibold" style={label}>Cancel</button>
        </div>
      )}

      {/* Phase 2: questions */}
      {phase === "questions" && (
        <div>
          <h2 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>
            {verifying ? "Let's verify your existing setup" : "A few questions first"}
          </h2>
          {a.registered_status === "not_sure" && (
            <p className="text-sm mt-1" style={label}>
              Not sure? If you've never filed paperwork with your state, you're most likely operating as a sole proprietorship by default. Answer below and we'll sort it out.
            </p>
          )}
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-[0.1em]" style={label}>Business name</label>
              <input data-testid="wizard-business-name" value={a.business_name} onChange={(e) => setA((f) => ({ ...f, business_name: e.target.value }))}
                placeholder="Acme Ventures LLC" className="w-full mt-1.5 px-4 py-2.5 rounded-md border bg-white text-sm outline-none" style={{ ...card, color: "var(--bk-text)" }} />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.1em]" style={label}>State of formation / operation</label>
              <div className="relative mt-1.5">
                <select data-testid="wizard-state" value={a.state} onChange={(e) => setA((f) => ({ ...f, state: e.target.value }))}
                  className="w-full appearance-none px-4 py-2.5 rounded-md border bg-white text-sm outline-none pr-9" style={{ ...card, color: a.state ? "var(--bk-text)" : "var(--bk-text-2)" }}>
                  <option value="">Choose a state…</option>
                  {cfg.states.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <CaretDown size={16} weight="bold" className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" color="var(--bk-text-2)" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.1em]" style={label}>Number of owners</label>
              <input data-testid="wizard-owners" type="number" min={1} max={100} value={a.num_owners}
                onChange={(e) => setA((f) => ({ ...f, num_owners: Math.max(1, parseInt(e.target.value || "1", 10)), structure: "" }))}
                className="w-full mt-1.5 px-4 py-2.5 rounded-md border bg-white text-sm outline-none" style={{ ...card, color: "var(--bk-text)" }} />
            </div>
            {[
              ["already_operating", "Is the business already operating?"],
              ["will_have_employees", "Will it have employees?"],
              ["personal_services", "Do you personally provide the services?"],
              ["professional_advice", "Has a CPA or attorney advised you?"],
              ["wellness_services", "Will you offer regulated wellness services (red-light therapy, hyperbaric)?"],
            ].map(([k, q]) => (
              <div key={k}>
                <label className="text-xs font-semibold uppercase tracking-[0.1em]" style={label}>{q}</label>
                <BoolPick value={a[k]} onChange={(v) => setA((f) => ({ ...f, [k]: v }))} testid={`wizard-${k}`} />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-5">
            <button onClick={() => setPhase("structure")} data-testid="wizard-next-structure" className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold">
              Next <ArrowRight size={16} weight="bold" />
            </button>
            <button onClick={() => setPhase("registered")} className="px-4 py-2.5 rounded-full text-sm font-semibold" style={{ ...label, border: "1px solid var(--bk-border)" }}>Back</button>
          </div>
        </div>
      )}

      {/* Phase 3: structure + tax treatment */}
      {phase === "structure" && (
        <div>
          <div className="flex items-center gap-2">
            <Scales size={22} weight="duotone" color="var(--bk-accent)" />
            <h2 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>
              {verifying ? "Which legal structure do you have?" : "Choose a legal structure"}
            </h2>
          </div>
          <p className="text-sm mt-1" style={label}>These are the legal structures. How you're taxed is a separate decision, below.</p>
          <div className="grid sm:grid-cols-2 gap-3 mt-4" data-testid="wizard-structures">
            {structures.map((s) => (
              <button key={s.key} type="button" data-testid={`wizard-structure-${s.key}`}
                onClick={() => setA((f) => ({ ...f, structure: s.key, tax_treatment: s.s_corp_eligible ? f.tax_treatment : "default" }))}
                className="rounded-xl border p-4 text-left transition-shadow hover:shadow-sm"
                style={{ borderColor: a.structure === s.key ? "var(--bk-accent)" : "var(--bk-border)", borderWidth: a.structure === s.key ? 2 : 1 }}>
                <p className="text-sm font-bold" style={{ color: "var(--bk-text)" }}>{s.label}</p>
                <p className="text-xs mt-1 leading-relaxed" style={label}>{s.description}</p>
              </button>
            ))}
          </div>

          <div className="mt-6 rounded-xl border p-4" style={card} data-testid="wizard-tax-treatment">
            <p className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-accent)" }}>Federal tax treatment — a separate decision</p>
            <p className="text-xs mt-1.5 leading-relaxed" style={label}>{cfg.s_corp_note}</p>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              {cfg.tax_treatments.map((t) => {
                const disabled = t.key === "s_corp_election" && chosen && !chosen.s_corp_eligible;
                return (
                  <button key={t.key} type="button" disabled={disabled} data-testid={`wizard-tax-${t.key}`}
                    onClick={() => setA((f) => ({ ...f, tax_treatment: t.key }))}
                    className="rounded-lg border p-3 text-left disabled:opacity-40"
                    style={{ borderColor: a.tax_treatment === t.key ? "var(--bk-accent)" : "var(--bk-border)", borderWidth: a.tax_treatment === t.key ? 2 : 1 }}>
                    <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{t.label}</p>
                    <p className="text-xs mt-1 leading-relaxed" style={label}>{t.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-2 mt-5">
            <button onClick={generate} disabled={busy} data-testid="wizard-generate-checklist" className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60">
              {busy ? "Building…" : "Build my checklist"} <ListChecks size={16} weight="bold" />
            </button>
            <button onClick={() => setPhase("questions")} className="px-4 py-2.5 rounded-full text-sm font-semibold" style={{ ...label, border: "1px solid var(--bk-border)" }}>Back</button>
          </div>
        </div>
      )}

      {/* Phase 4: checklist + action paths */}
      {phase === "checklist" && result && (
        <div>
          <h2 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>
            {verifying ? `Verify your ${result.structure.label} paperwork` : `Your ${result.state || ""} ${result.structure.label} checklist`}
          </h2>
          <p className="text-sm mt-1" style={label}>{result.operating_note}</p>
          {verifying && (
            <p className="text-sm mt-2 font-semibold" style={{ color: "var(--bk-text)" }}>Confirm each of these is done or on file — no need to re-form anything:</p>
          )}

          {result.considerations.length > 0 && (
            <div className="mt-4 rounded-md p-3 space-y-1.5" style={{ background: "#F6EEDF" }} data-testid="wizard-considerations">
              {result.considerations.map((c, i) => (
                <p key={i} className="text-sm flex gap-2" style={{ color: "var(--bk-text)" }}>
                  <ShieldWarning size={16} weight="fill" color="#8A6A3B" className="shrink-0 mt-0.5" /> {c}
                </p>
              ))}
            </div>
          )}

          <ol className="space-y-3 mt-5" data-testid="wizard-checklist">
            {result.checklist.map((c, i) => (
              <li key={c.key} className="flex gap-3 text-sm" data-testid={`wizard-check-${c.key}`}>
                <span className="font-mono text-xs mt-0.5 shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }}>{i + 1}</span>
                <span>
                  <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{c.title}</span>
                  <span className="block text-xs mt-0.5 leading-relaxed" style={label}>{c.detail}</span>
                </span>
              </li>
            ))}
          </ol>

          {/* Action paths */}
          <div className="flex flex-wrap gap-2 mt-6" data-testid="wizard-action-paths">
            {result.state_filing_site && (
              <>
                <a href={result.state_filing_site} target="_blank" rel="noopener noreferrer" data-testid="wizard-action-check-name"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border" style={{ ...card, color: "var(--bk-accent)" }}>
                  Check name availability <ArrowSquareOut size={14} weight="bold" />
                </a>
                <a href={result.state_filing_site} target="_blank" rel="noopener noreferrer" data-testid="wizard-action-state-site"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border" style={{ ...card, color: "var(--bk-accent)" }}>
                  Official {result.state} filing site <ArrowSquareOut size={14} weight="bold" />
                </a>
              </>
            )}
            <button onClick={() => { toast.info("Open any business → Vault tab to upload your formation documents."); navigate("/dashboard"); }} data-testid="wizard-action-vault"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border" style={{ ...card, color: "var(--bk-text)" }}>
              <UploadSimple size={15} weight="bold" /> Upload documents to the Vault
            </button>
            <button onClick={() => save(true)} disabled={busy} data-testid="wizard-action-already-done"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border disabled:opacity-60" style={{ ...card, color: "var(--bk-success)" }}>
              <CheckCircle size={15} weight="fill" /> I already completed this
            </button>
            <button onClick={() => toast.info("Good call. A CPA or attorney should review structure and tax elections — bring this checklist to the meeting." + (partner?.name ? ` You can also use ${partner.name} for the filing itself.` : ""))} data-testid="wizard-action-professional"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border" style={{ ...card, color: "var(--bk-text)" }}>
              <GraduationCap size={15} weight="bold" /> I need professional guidance
            </button>
          </div>

          {/* Wellness compliance path */}
          {result.wellness_compliance && (
            <div className="mt-6 rounded-xl border p-4" style={{ borderColor: "#B23B1F" }} data-testid="wizard-wellness-compliance">
              <div className="flex items-center gap-2">
                <HeartStraight size={20} weight="duotone" color="#B23B1F" />
                <p className="text-sm font-bold" style={{ color: "var(--bk-text)" }}>{result.wellness_compliance.title}</p>
              </div>
              <p className="text-xs mt-1.5 leading-relaxed font-semibold" style={{ color: "#B23B1F" }}>{result.wellness_compliance.disclaimer}</p>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                {result.wellness_compliance.sections.map((sec) => (
                  <div key={sec.key} data-testid={`wellness-section-${sec.key}`}>
                    <p className="text-xs font-bold uppercase tracking-wide" style={{ color: "var(--bk-text)" }}>{sec.title}</p>
                    <ul className="mt-1 space-y-0.5">
                      {sec.items.map((it, i) => (
                        <li key={i} className="text-xs leading-relaxed flex gap-1.5" style={label}><span>•</span>{it}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs mt-5 italic" style={label} data-testid="wizard-privacy-note">{cfg.privacy_note}</p>

          <div className="flex items-center gap-2 mt-4">
            {!verifying && (
              <button onClick={() => save(false)} disabled={busy} data-testid="wizard-save-btn" className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60">
                {busy ? "Saving…" : "Save & start tracking"}
              </button>
            )}
            <button onClick={() => setPhase("structure")} className="px-4 py-2.5 rounded-full text-sm font-semibold" style={{ ...label, border: "1px solid var(--bk-border)" }}>Back</button>
            <button onClick={onCancel} className="px-4 py-2.5 rounded-full text-sm font-semibold" style={label}>Close</button>
          </div>
        </div>
      )}
    </section>
  );
}
